import { PaywallProvider } from '@frontend/react-domain';
import {
  useAnalytics,
  useEditProfileMutation,
  useLazyRetrievePromotionDetailsQuery,
  useRetrieveClientQuery,
  useRetrieveClientStatusQuery,
} from '@frontend/web/hooks';
import { handleMutation } from '@frontend/web/utils';
import { StripeLoading, StripeProvider, StripeUnavailable } from '../../Payment';
import { PaywallFooter } from '../PaywallFooter';
import { PaywallModalBody } from './PaywallModalBody';
import { PaywallModalView } from './PaywallModalView';
import { PaywallPaymentScope } from './PaywallPaymentScope';

const EMPTY_PLAN_LOOKUP = {};

export type PaywallModalProps = {
  isFrictionlessUser?: boolean;
  isOpen: boolean;
  minimumCredits?: number;
  onClose: () => void;
  onSuccess: () => void;
  shouldCompleteOnboardingOnClose?: boolean;
};

export const PaywallModal = ({
  isFrictionlessUser = false,
  isOpen,
  minimumCredits,
  onClose,
  onSuccess,
  shouldCompleteOnboardingOnClose = false,
}: PaywallModalProps) => {
  const retrieveClientHookResult = useRetrieveClientQuery();
  const retrievePromotionDetailsHookResult = useLazyRetrievePromotionDetailsQuery();
  const retrieveClientStatusHookResult = useRetrieveClientStatusQuery();
  const editProfileHookResult = useEditProfileMutation();
  const { track } = useAnalytics();

  return (
    <PaywallProvider
      editProfileHookResult={editProfileHookResult}
      handleMutation={handleMutation}
      isFrictionlessUser={isFrictionlessUser}
      isOpen={isOpen}
      minimumCredits={minimumCredits}
      onClose={onClose}
      onComplete={onSuccess}
      planLookup={EMPTY_PLAN_LOOKUP}
      retrieveClientHookResult={retrieveClientHookResult}
      retrieveClientStatusHookResult={retrieveClientStatusHookResult}
      retrievePromotionDetailsHookResult={retrievePromotionDetailsHookResult}
      shouldCompleteOnboardingOnClose={shouldCompleteOnboardingOnClose}
      track={track}
    >
      <StripeProvider
        expectedElements={[]}
        loadingFallback={
          <PaywallModalView isOpen={isOpen} onClose={onClose}>
            <StripeLoading />
          </PaywallModalView>
        }
        reloadFallback={
          <PaywallModalView isOpen={isOpen} onClose={onClose}>
            <StripeUnavailable onAction={() => window.location.reload()} variant="reload" />
          </PaywallModalView>
        }
        retryFallback={(onRetry) => (
          <PaywallModalView isOpen={isOpen} onClose={onClose}>
            <StripeUnavailable onAction={onRetry} variant="retry" />
          </PaywallModalView>
        )}
      >
        <PaywallPaymentScope>
          <PaywallModalView Footer={<PaywallFooter />} isOpen={isOpen} onClose={onClose}>
            <PaywallModalBody onClose={onClose} />
          </PaywallModalView>
        </PaywallPaymentScope>
      </StripeProvider>
    </PaywallProvider>
  );
};
